import { DropdownContent, DropdownMenuItem } from "@bcc-ui/ui-components";
import * as React from "react";
import { useTranslation } from "react-i18next";

import FileSharingErrorDialog from "./FileSharingErrorDialog";
import GetDownloadLinkModal from "./GetDownloadLinkModal";
import MoveFileModal from "./MoveFileModal";
import RenameFileModal from "./RenameFileModal";
import SimpleConfirmationDialog from "./SimpleConfirmationDialog";
import { useAbortControllers } from "./useAbortControllers";
import { AUTH_FILESHARE_DELETE, AUTH_FILESHARE_EDIT, AUTH_FILESHARE_VIEW } from "domain/authority";
import { hasHttpStatus, SharedFile } from "domain/fileSharing";
import { fileSharingService } from "services/file-sharing/FileSharingService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { userSessionService } from "services/user/UserSessionService";
import { logger } from "utils/logging";

import testIds from "testIds.json";

export function getDownloadErrorKey(error: Error): string {
    const status = hasHttpStatus(error) ? error.status : undefined;
    if (status === 429) {
        return "FileSharing.download.errorQuotaExceeded";
    }
    if (status === 404) {
        return "FileSharing.download.errorNotFound";
    }
    return "FileSharing.download.errorGeneric";
}

interface Props {
    item: SharedFile;
    existingPaths: string[];
    onDeleted: () => void;
    onMoved: () => void;
    onRenamed: () => void;
    onDownloaded?: () => void;
}

const FileSharingMenuItems = ({
    item,
    existingPaths,
    onDeleted,
    onMoved,
    onRenamed,
    onDownloaded,
}: Props): JSX.Element => {
    const { t } = useTranslation();
    const abortControllers = useAbortControllers();
    const canView = userSessionService.userHasAllAuthorities([AUTH_FILESHARE_VIEW]);
    const canEdit = userSessionService.userHasAllAuthorities([AUTH_FILESHARE_EDIT]);
    const canDelete = userSessionService.userHasAllAuthorities([AUTH_FILESHARE_DELETE]);

    const [deleteOpen, setDeleteOpen] = React.useState(false);
    const [deleteLoading, setDeleteLoading] = React.useState(false);
    const [deleteError, setDeleteError] = React.useState<string | undefined>();
    const [downloadError, setDownloadError] = React.useState<string | undefined>();
    const [downloadLinkOpen, setDownloadLinkOpen] = React.useState(false);
    const [downloadLinkUrl, setDownloadLinkUrl] = React.useState<string | undefined>();
    const [downloadLinkLoading, setDownloadLinkLoading] = React.useState(false);
    const downloadLinkAbortControllerRef = React.useRef<AbortController | null>(null);
    const [moveOpen, setMoveOpen] = React.useState(false);
    const [renameOpen, setRenameOpen] = React.useState(false);

    const handleDelete = React.useCallback(() => {
        usageStatisticsService.sendEvent({ category: Category.FILE_SHARING, action: Action.REMOVE_FILE });
        const abortController = new AbortController();
        abortControllers.push(abortController);
        setDeleteLoading(true);
        fileSharingService
            .deleteFile(item.uuid, abortController)
            .then(() => {
                setDeleteOpen(false);
                onDeleted();
            })
            .catch((error: Error) => {
                if (!abortController.signal.aborted) {
                    logger.error("Failed to delete shared file", error);
                    setDeleteError(t("FileSharing.delete.errorMessage"));
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setDeleteLoading(false);
                }
            });
    }, [abortControllers, item.uuid, onDeleted, t]);

    const handleDownload = React.useCallback(() => {
        usageStatisticsService.sendEvent({ category: Category.FILE_SHARING, action: Action.DOWNLOAD_FILE });
        const abortController = new AbortController();
        abortControllers.push(abortController);
        fileSharingService
            .getDownloadUrl(item.uuid, abortController)
            .then(({ presigned_url }) => {
                window.open(presigned_url, "_blank", "noopener,noreferrer");
                onDownloaded?.();
            })
            .catch((error: Error) => {
                if (!abortController.signal.aborted) {
                    logger.error("Failed to get download URL for shared file", error);
                    setDownloadError(t(getDownloadErrorKey(error)));
                }
            });
    }, [abortControllers, onDownloaded, item.uuid, t]);

    const handleGetDownloadLink = React.useCallback(() => {
        usageStatisticsService.sendEvent({ category: Category.FILE_SHARING, action: Action.DOWNLOAD_FILE });
        const abortController = new AbortController();
        abortControllers.push(abortController);
        downloadLinkAbortControllerRef.current = abortController;
        setDownloadLinkUrl(undefined);
        setDownloadLinkLoading(true);
        setDownloadLinkOpen(true);
        fileSharingService
            .getDownloadUrl(item.uuid, abortController)
            .then(({ presigned_url }) => {
                if (!abortController.signal.aborted) {
                    setDownloadLinkUrl(presigned_url);
                    onDownloaded?.();
                }
            })
            .catch((error: Error) => {
                if (!abortController.signal.aborted) {
                    logger.error("Failed to get download URL for shared file", error);
                    setDownloadLinkOpen(false);
                    setDownloadError(t(getDownloadErrorKey(error)));
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setDownloadLinkLoading(false);
                }
            });
    }, [abortControllers, onDownloaded, item.uuid, t]);

    const handleDownloadLinkClose = React.useCallback(() => {
        downloadLinkAbortControllerRef.current?.abort();
        downloadLinkAbortControllerRef.current = null;
        setDownloadLinkOpen(false);
        setDownloadLinkUrl(undefined);
        // finally, body is a no-op when aborted, so reset the loading state explicitly here
        setDownloadLinkLoading(false);
    }, []);

    return (
        <>
            <DropdownContent testId={testIds.common.primaryView.table.kebabMenu.itself}>
                {canView && (
                    <DropdownMenuItem
                        onClick={handleDownload}
                        testId={testIds.workArea.fileSharing.table.kebabMenu.downloadButton}
                    >
                        {t("FileSharing.menu.download")}
                    </DropdownMenuItem>
                )}
                {canView && (
                    <DropdownMenuItem
                        onClick={handleGetDownloadLink}
                        testId={testIds.workArea.fileSharing.table.kebabMenu.getDownloadLinkButton}
                    >
                        {t("FileSharing.menu.getDownloadLink")}
                    </DropdownMenuItem>
                )}
                {canEdit && (
                    <DropdownMenuItem
                        onClick={() => setMoveOpen(true)}
                        testId={testIds.workArea.fileSharing.table.kebabMenu.moveButton}
                    >
                        {t("FileSharing.menu.move")}
                    </DropdownMenuItem>
                )}
                {canEdit && (
                    <DropdownMenuItem
                        onClick={() => setRenameOpen(true)}
                        testId={testIds.workArea.fileSharing.table.kebabMenu.renameButton}
                    >
                        {t("FileSharing.menu.rename")}
                    </DropdownMenuItem>
                )}
                {canDelete && (
                    <DropdownMenuItem
                        errorStyle
                        onClick={() => {
                            setDeleteError(undefined);
                            setDeleteOpen(true);
                        }}
                        testId={testIds.workArea.fileSharing.table.kebabMenu.deleteButton}
                    >
                        {t("FileSharing.menu.delete")}
                    </DropdownMenuItem>
                )}
            </DropdownContent>

            <SimpleConfirmationDialog
                isOpen={deleteOpen}
                title={t("FileSharing.delete.title", { name: item.filename })}
                body={t("FileSharing.delete.body")}
                isLoading={deleteLoading}
                errorMessage={deleteError}
                onClose={() => {
                    setDeleteError(undefined);
                    setDeleteOpen(false);
                }}
                onConfirm={handleDelete}
            />

            {downloadError && (
                <FileSharingErrorDialog
                    error={downloadError}
                    headingText={t("FileSharing.download.errorTitle")}
                    onClose={() => setDownloadError(undefined)}
                />
            )}

            <MoveFileModal
                item={item}
                existingPaths={existingPaths}
                isOpen={moveOpen}
                onClose={() => setMoveOpen(false)}
                onMoved={onMoved}
            />

            <RenameFileModal
                item={item}
                isOpen={renameOpen}
                onClose={() => setRenameOpen(false)}
                onRenamed={onRenamed}
            />

            <GetDownloadLinkModal
                presignedUrl={downloadLinkUrl}
                isLoading={downloadLinkLoading}
                isOpen={downloadLinkOpen}
                onClose={handleDownloadLinkClose}
            />
        </>
    );
};

export default FileSharingMenuItems;
